import { useState } from 'react';
import { Input, cn } from '@bliks-io/ui';

import { parseSeatLimit, seatLimitToInput, type ParsedSeatLimit } from '@/lib/seat-limits';

interface SeatLimitInputProps {
  /** The cap on file — what the field shows until it is edited. */
  stored: number | null | undefined;
  /** Every keystroke's parse result, so the owner can gate its own save. */
  onParsed: (parsed: ParsedSeatLimit) => void;
  id?: string;
  className?: string;
  disabled?: boolean;
  /** Commit-on-blur editors pass this; it also makes Enter blur the field. */
  onCommit?: () => void;
}

/**
 * One seat cap, edited in place.
 *
 * Text rather than type="number": a number input reports an unparseable entry
 * ("abc", a lone "-") as an EMPTY value, which every seat editor reads as
 * "unlimited" — so a typo would silently clear a real cap. As text the raw
 * string reaches parseSeatLimit, which refuses it and blocks the save.
 */
export function SeatLimitInput({
  stored,
  onParsed,
  id,
  className,
  disabled,
  onCommit
}: SeatLimitInputProps) {
  const [draft, setDraft] = useState<string | undefined>(undefined);

  const value = draft ?? seatLimitToInput(stored);
  const parsed = parseSeatLimit(value);

  return (
    <Input
      id={id}
      type="text"
      inputMode="numeric"
      className={cn(!parsed.valid && 'border-destructive', className)}
      value={value}
      disabled={disabled}
      onChange={(event) => {
        setDraft(event.target.value);
        onParsed(parseSeatLimit(event.target.value));
      }}
      onBlur={onCommit}
      onKeyDown={
        onCommit &&
        ((event) => {
          if (event.key === 'Enter') event.currentTarget.blur();
        })
      }
    />
  );
}
